import classNames from "classnames";
import { useRouter } from "next/router";
import { useMemo } from "react";
import { ShopHead } from "@components/CommonHead";
import CommonLayout from "@components/CommonLayout";
import Pager from "@components/Pager";
import { Layout, NameArea } from "@components/ShopDetail";
import { ShopMatchCard } from "@components/ShopMatchCard";
import { usePagination } from "@hooks/usePagination";
import { queryForSSP } from "@libs/apolloClient";
import { paths } from "@libs/paths";
import { setUuidFromQuery } from "@libs/utils/query";
import {
  ShopMatches_ShopDocument,
  useShopMatches_ShopMatchQuery,
} from "./__generated__/queries";
import styles from "./index.module.css";
import type { ShopMatches_ShopQueryResult } from "./__generated__/queries";
import type { ApolloError } from "@apollo/client";
import type { GetServerSideProps, NextPage } from "next";

type MatchesProps = {
  data?: ShopMatches_ShopQueryResult["data"];
  error?: ApolloError;
};

const DEFAULT_PER_PAGE = 10;

const ShopMatches: NextPage<MatchesProps> = (props) => {
  const { data } = props;

  const { query } = useRouter();
  const { currentPage, navigate } = usePagination();

  const shopUuid = useMemo(() => {
    return setUuidFromQuery(query.uuid) || "";
  }, [query.uuid]);

  const { data: shopMatchData } = useShopMatches_ShopMatchQuery({
    variables: {
      shopUuid: shopUuid,
      page: currentPage,
      perPage: DEFAULT_PER_PAGE,
    },
    onError: console.error,
  });

  if (!data || !shopMatchData) {
    return null;
  }

  const shopHeadOverrides = { canonicalUrl: paths.shopMatches(shopUuid) };
  const shopMatches = shopMatchData.shopMatch.nodes;

  return (
    <CommonLayout hasFooter footerPC>
      <ShopHead
        shop={data?.shop}
        type="matches"
        overrides={shopHeadOverrides}
      />

      <div className={styles.main}>
        {data && (
          <>
            <div className={classNames(styles.top, "none md:block")}>
              <NameArea shop={data.shop} />
            </div>

            <Layout shop={data.shop} current="shopMatch">
              <section className={styles.schedule}>
                <div
                  className={classNames(
                    styles.columnInner,
                    "l-main__innerWidth"
                  )}
                >
                  <div className={styles.scheduleTop}>
                    <h1 className={`${styles.contentTitle} c-contentTitle`}>
                      上映予定
                    </h1>
                  </div>

                  <div
                    className={classNames(styles.matchList, {
                      [styles.matchListExist]: shopMatches.length > 0,
                    })}
                  >
                    {shopMatches.map((shopMatch) => (
                      <ShopMatchCard
                        key={shopMatch.id}
                        shopUuid={shopUuid}
                        shopName={data.shop.name}
                        shopTel={data.shop.tel}
                        shopTelWithHyphen={data.shop.telWithHyphen}
                        shopMatch={shopMatch}
                      />
                    ))}
                  </div>

                  {shopMatches.length > 0 ? (
                    <Pager
                      onNavigation={navigate}
                      totalPage={shopMatchData.shopMatch.totalPage}
                      hasPreviousPage={shopMatchData.shopMatch.hasPreviousPage}
                      hasNextPage={shopMatchData.shopMatch.hasNextPage}
                      currentPage={currentPage}
                    />
                  ) : (
                    <p className={styles.lead}>上映予定の試合はありません</p>
                  )}
                </div>
              </section>
            </Layout>
          </>
        )}
      </div>
    </CommonLayout>
  );
};

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { params } = context;
  return queryForSSP({
    query: ShopMatches_ShopDocument,
    variables: {
      shopUuid: params?.uuid || "",
    },
  });
};

export default ShopMatches;
